import { useCallback, useEffect, useRef, useState } from 'react'
import type { PaymentWalletOption, Stripe, StripeElementsUpdateOptions } from '@stripe/stripe-js'
import { PaymentElement, useElements } from '@stripe/react-stripe-js'
import {
  appendLuxPlusBackupCard,
  prepareLuxPlusCheckoutCardBackupCandidate,
  type LuxPlusCheckoutCardBackupCandidate,
} from 'checkout/lib/luxPlusCheckoutCardBackup'
import styled from 'styled-components'

const StripePaymentElement = styled(PaymentElement)`
  border: none;
`

const stripePaymentElementOptions = {
  layout: 'tabs' as const,
  wallets: {
    applePay: 'never' as PaymentWalletOption,
    googlePay: 'never' as PaymentWalletOption,
    link: 'never' as PaymentWalletOption,
  },
}

interface Props {
  account: App.AuthState['account']
  currencyCode: string
  onCompleteChange: (complete: boolean) => void
  onPreparedPaymentMethodChange: (candidate?: LuxPlusCheckoutCardBackupCandidate) => void
  onPreparingPaymentMethodChange: (preparing: boolean) => void
  onSubmitHandlerChange: (handler?: () => Promise<void>) => void
  purposeId: string
  regionCode: string
  selected: boolean
  stripe: Stripe
}

function LuxPlusAddBackupCardElement(props: Props) {
  const {
    account,
    currencyCode,
    onCompleteChange,
    onPreparedPaymentMethodChange,
    onPreparingPaymentMethodChange,
    onSubmitHandlerChange,
    purposeId,
    regionCode,
    selected,
    stripe,
  } = props
  const elements = useElements()
  const [preparedPaymentMethod, setPreparedPaymentMethod] = useState<LuxPlusCheckoutCardBackupCandidate | undefined>()
  const [paymentElementComplete, setPaymentElementComplete] = useState(false)
  const preparePaymentMethodRequestRef = useRef(0)

  const submitNewCard = useCallback(async() => {
    if (!selected || !elements || !stripe) return

    elements.update({ setupFutureUsage: 'off_session' } as StripeElementsUpdateOptions)

    let paymentMethodId = preparedPaymentMethod?.paymentMethodId
    if (paymentMethodId) {
      const { error: submitError } = await elements.submit()

      if (submitError) {
        throw new Error(submitError.message)
      }
    } else {
      paymentMethodId = (await prepareLuxPlusCheckoutCardBackupCandidate({
        account,
        elements,
        stripe,
      })).paymentMethodId
    }

    if (!paymentMethodId) {
      throw new Error('Missing LuxPlus backup payment method ID')
    }

    await appendLuxPlusBackupCard({
      currencyCode,
      elements,
      paymentMethodId,
      purposeId,
      regionCode,
      stripe,
    })
  }, [account, currencyCode, elements, preparedPaymentMethod?.paymentMethodId, purposeId, regionCode, selected, stripe])

  useEffect(() => {
    if (!selected || !paymentElementComplete || !elements || !stripe) {
      setPreparedPaymentMethod(undefined)
      onPreparedPaymentMethodChange(undefined)
      onPreparingPaymentMethodChange(false)
      return
    }

    const requestId = preparePaymentMethodRequestRef.current + 1
    preparePaymentMethodRequestRef.current = requestId
    const stripeElements = elements
    const stripeInstance = stripe

    onPreparingPaymentMethodChange(true)
    prepareLuxPlusCheckoutCardBackupCandidate({
      account,
      elements: stripeElements,
      stripe: stripeInstance,
    }).then(candidate => {
      if (preparePaymentMethodRequestRef.current === requestId) {
        setPreparedPaymentMethod(candidate)
        onPreparedPaymentMethodChange(candidate)
        onPreparingPaymentMethodChange(false)
      }
    }).catch(() => {
      if (preparePaymentMethodRequestRef.current === requestId) {
        setPreparedPaymentMethod(undefined)
        onPreparedPaymentMethodChange(undefined)
        onPreparingPaymentMethodChange(false)
      }
    })
  }, [account, elements, onPreparedPaymentMethodChange, onPreparingPaymentMethodChange, paymentElementComplete, selected, stripe])

  useEffect(() => {
    onSubmitHandlerChange(selected ? submitNewCard : undefined)

    return () => {
      onSubmitHandlerChange(undefined)
    }
  }, [onSubmitHandlerChange, selected, submitNewCard])

  return <StripePaymentElement
    onChange={event => {
      setPreparedPaymentMethod(undefined)
      onPreparedPaymentMethodChange(undefined)
      onPreparingPaymentMethodChange(event.complete)
      setPaymentElementComplete(event.complete)
      onCompleteChange(event.complete)
    }}
    options={stripePaymentElementOptions}
  />
}

export default LuxPlusAddBackupCardElement
